import React, { useState } from 'react';
import { Button, Modal, Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import NewModelForm from './NewModelForm';
import axios from 'axios';

const isRequiredInput = (inputs, name) => {
  const input = inputs?.[name];
  return input === true || input?.required === true;
};

const parseModelFileNames = (value) => [...new Set(
  String(value || '')
    .replaceAll(',', '\n')
    .split('\n')
    .map((fileName) => fileName.trim())
    .filter(Boolean)
)];

const ImportNewModelButton = ({ refreshModels, model }) => {
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const isEditMode = Boolean(model);
  const isTextPromptModel = isRequiredInput(model?.parameter_schema?.inputs, 'text');
  const initialValues = isEditMode ? {
    modelName: model.name || '',
    modelFileName: (Array.isArray(model.model_file_name)
      ? model.model_file_name
      : [model.model_file_name]).filter(Boolean).join(', '),
    description: model.info?.description || '',
    category: model.info?.category || '',
    githubURL: model.github_url || '',
    modelTestImages: isTextPromptModel ? model.test_images?.[0] || '' : [],
    textPromptInput: isTextPromptModel,
  } : {
    modelName: '',
    modelFileName: '',
    description: '',
    category: '',
    githubURL: '',
    modelTestImages: [],
    textPromptInput: false,
  };

const handleSubmit = async (v4p_modelInfoForm) => {
  if (!v4p_modelInfoForm.category) {
    alert('Please select a model category.');
    return;
  }

  if (isEditMode) {
    try {
      let response;
      const isTextPrompt = Boolean(v4p_modelInfoForm.textPromptInput);
      const testImages = v4p_modelInfoForm.modelTestImages || [];
      const modelFileNames = parseModelFileNames(v4p_modelInfoForm.modelFileName);

      if (isTextPrompt ? testImages.trim() : testImages.length > 0) {
        const testFormData = new FormData();
        if (isTextPrompt) {
          testFormData.append("text_prompt", testImages.trim());
        } else {
          testImages.forEach((file) => testFormData.append("test_images", file));
        }
        testFormData.append("replace_existing", "true");
        response = await axios.post(`/v4p_models/${model.id}/test_images`, testFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const metadataResponse = await axios.patch(`/v4p_models/${model.id}`, {
        name: v4p_modelInfoForm.modelName,
        model_file_name: modelFileNames.length === 1 ? modelFileNames[0] : modelFileNames,
        info: {
          ...(model.info || {}),
          description: v4p_modelInfoForm.description,
          category: v4p_modelInfoForm.category,
          public: Boolean(model.info?.public),
        },
        github_url: v4p_modelInfoForm.githubURL,
      });

      handleCloseModal();
      refreshModels?.(metadataResponse.data || response?.data);
    } catch (error) {
      console.error('Error:', error.response?.data || error.message);
      alert(`Something went wrong: ${error.response?.data?.detail || error.message}`);
    }
    return;
  }

  const isTextPrompt = Boolean(v4p_modelInfoForm.textPromptInput);
  const testImages = v4p_modelInfoForm.modelTestImages || [];
  const hasTestData = isTextPrompt
    ? typeof testImages === 'string' && testImages.trim().length > 0
    : testImages.length >= 1 && testImages.length <= 3;
  const modelFileNames = parseModelFileNames(v4p_modelInfoForm.modelFileName);

  if (
    !v4p_modelInfoForm.modelName.trim() ||
    modelFileNames.length === 0 ||
    !v4p_modelInfoForm.description.trim() ||
    !v4p_modelInfoForm.githubURL.trim() ||
    !hasTestData
  ) {
    alert('Please fill in all the required fields.');
    return;
  }

  try {
    // Step 1: Create v4p_model
    const createResponse = await axios.post('/v4p_models', {
      name: v4p_modelInfoForm.modelName,
      model_file_name: modelFileNames.length === 1 ? modelFileNames[0] : modelFileNames,
      info: {
        description: v4p_modelInfoForm.description,
        category: v4p_modelInfoForm.category,
        public: false,
      },
      github_url: v4p_modelInfoForm.githubURL,
    });

    const modelId = createResponse.data.id;



    // Step 3: Upload test images if present
    const testImages = v4p_modelInfoForm.modelTestImages || [];

    if (testImages.length > 0) {
      const testFormData = new FormData();
      if (isTextPrompt) {
        testFormData.append("text_prompt", testImages.trim());
      } else {
        testImages.forEach((file) => testFormData.append("test_images", file));
      }

      await axios.post(`/v4p_models/${modelId}/test_images`, testFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    }

    handleCloseModal();
    refreshModels?.();

  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
    alert(`Something went wrong: ${error.response?.data?.detail || error.message}`);
  }
};

  return (
    <>
      {isEditMode ? (
        <Tooltip title="Edit model settings">
          <IconButton
            aria-label={`Edit ${model.name}`}
            onClick={handleOpenModal}
            size="small"
            sx={{ color: '#16a4b0' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : <Button
        variant="contained"
        sx={{
          borderRadius: '12px',
          backgroundColor: '#16a4b0',
          color: 'white',
          textTransform: 'none',
          fontSize: '16px',
          width: { xs: '90%', sm: 'auto' },
          ml: 2,
        }}
        onClick={handleOpenModal}
      >
        Import New Model
      </Button>}

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            p: 4,
            borderRadius: 3,
            width: { xs: '90%', sm: '40%' },
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <NewModelForm
            key={model?.id || 'new-model'}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            showFileInput={!isEditMode}
            showTestImages
            testImagesRequired={!isEditMode}
            existingTestImages={model?.test_images || []}
            title={isEditMode ? 'Model Info' : 'New Model Info'}
            submitLabel={isEditMode ? 'Apply changes' : 'Import'}
            handleCloseModal={handleCloseModal}
          />
        </Box>
      </Modal>
    </>
  );
};

export default ImportNewModelButton;
